import { AppsNamesType, AppsWithMoreInfoType } from "@whatslly/types/AppsNames";
import uniq from "lodash/uniq";
import { ActiveAction, ActiveActions } from "@whatslly/types/integrations/appsActions/AppsActions";
import type { IStorage } from "./storage.constants";
import { InjectStorage, STORAGE } from "./storage.constants";
import { inject, injectable } from "inversify";

@injectable()
export class AppStorage {
  constructor(@inject(InjectStorage) private storage: IStorage) {}

  private async getListOfActiveApps(): Promise<AppsNamesType[]> {
    const list = await this.storage.get(STORAGE.ACTIVE_APPS_LIST);
    return list || [];
  }

  public async getActiveApps(): Promise<Record<AppsWithMoreInfoType, ActiveAction>> {
    const listOfActiveApps = await this.getListOfActiveApps();
    const list = await Promise.all(
      listOfActiveApps.map(async (appName) => {
        const data = await this.storage.get(this.getAppStorageKey(appName));
        try {
          if (data) {
            return { appName, data: data };
          }
          return { appName, data: undefined };
        } catch {
          return { appName, data: undefined };
        }
      })
    );

    return list.reduce<ActiveActions>((all, item) => {
      if (typeof item.data === "undefined") {
        return all;
      }
      //FIXME
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
      all[item.appName] = item.data as any;
      return all;
    }, {} as ActiveActions);
  }

  public async getAppByName(appName: AppsNamesType): Promise<Partial<ActiveAction>> {
    try {
      return (await this.storage.get(this.getAppStorageKey(appName))) ?? {};
    } catch {
      return {};
    }
  }

  private getAppStorageKey(appName: AppsNamesType) {
    return `${STORAGE.ACTIVE_APPS}:${appName}` as const;
  }

  private async removeAppFromList(appName: AppsNamesType) {
    const activeAppList = await this.getListOfActiveApps();
    await this.storage.set(
      STORAGE.ACTIVE_APPS_LIST,
      activeAppList.filter((app) => app !== appName)
    );
  }

  private async addAppToAppsList(appName: AppsNamesType) {
    const activeAppList = await this.getListOfActiveApps();
    activeAppList.push(appName);
    await this.storage.set(STORAGE.ACTIVE_APPS_LIST, uniq(activeAppList));
  }

  public async removeActiveApps(appName: AppsNamesType) {
    await this.storage.remove(this.getAppStorageKey(appName));
    await this.removeAppFromList(appName);
  }

  public async addActiveApp<T extends Partial<ActiveAction>>(appName: AppsNamesType, data: T) {
    await this.addAppToAppsList(appName);
    await this.storage.set(this.getAppStorageKey(appName), data);
  }
}
